/**
 * 위험성평가 결과서 생성 시 포함할 점검을 선택하는 모달
 * - siteId가 있으면 해당 현장의 점검 목록 바로 로드
 * - siteId가 없으면 현장 선택 드롭다운 먼저 표시
 * - 체크박스 복수 선택 (COMPLETED / PENDING_REVIEW 상태만)
 * - 2개 이상 선택 시 정렬 순서 드롭다운 표시
 *
 * ## 서버 `reportGate`를 읽는다 — 이 화면은 게이트의 **독자**다 (2026-10-09)
 *
 * 후보를 `GET /inspections`(= `findAll`)에서 받으므로 응답에 점검별 `reportGate`가 실려 온다.
 * 그런데 종전엔 **한 번도 읽지 않았고** 게이트성 코드가 「0건 선택 금지」 하나뿐이었다 ⇒ 선택된
 * 개선대책이 **0건인 점검을 함께 고를 수 있었고 서버가 400으로 거부**했다(집행은 「고른 점검
 * **전부**가 선택 ≥1」 — `reports.service.ts`의 `assertSelectedMeasuresPresent`). 즉 사용자는
 * 고르고 확정한 뒤에야 거절당했다.
 *
 * ⚠️ **권한 축을 여기서 AND하지 않는 것이 의도다 — 행 메뉴와 다르다.**
 * 정본은 결정 `docs/decisions/2026-10-09-the-enforcement-expression-is-the-canonical-gate.md`다:
 * **집행 식이 정본이고 화면은 소비만 한다**(그 결정의 결론 1·축 ③) — 서버가 `canCreateReport`를
 * 집행 식으로 계산해 `reportGate`에 싣는다. 그 식은 **`reports.service.ts:1316`·`:1329`**이고
 * 두 항의 **OR**이며 **신원 축이 앞에 있다**:
 *
 * ```
 * [그 점검의 활성 참가자 ∨ 주담당자]                     ← 신원 축 (OR, `.some`)
 *   ∨
 * [(소유자 ∨ 그 현장 배정) ∧ (소유자 ∨ report.create)]    ← scope ∧ 키
 * ```
 *
 * ⇒ 이 화면에 권한식을 또 두면 그 결정이 **없애려는 사본의 네 번째**가 된다. 그리고 손으로 AND하면
 * **두 가지 방향으로** 집행보다 좁아진다: ⓐ 신원 축은 OR이라 **scope도 키도 없는 참가자**를 통과시키고
 * ⓑ 그 축은 **`.some`(고른 집합 중 하나라도)**이라 행마다 AND하는 것과 애초에 다른 연산이다 —
 * **남의 점검을 함께 묶는 것이 집행상 허용**이다. 그 결정의 prod 실측: 점검 **목록**의 손식 AND가
 * 집행 통과 **145건 중 46건을 거짓 차단**하고 있었다(= 할 수 있는 일을 숨긴 쪽이다). 게다가 이
 * 모달을 띄우는 자리(행 메뉴·상세·스케줄 메뉴)가 이미 자기 판정을 통과한 뒤라 2차 적용이기도 하다.
 *
 * ⚠️ **이 식과 수치의 정본은 `b2e9ef2c6` «이후»의 그 결정 기록이다 — 그 전 판은 신원 축이 빠져
 * 있었다**(식을 `(소유자 ∨ 배정) ∧ (소유자 ∨ report.create)`로, 수치를 144/45로 적고 「상세가
 * 집행보다 1건 넓다」까지 말했다. 정정판에서 상세는 **양방향 0건**이다). 그 반쪽 판을 인용한 주석이
 * 한 번 이 자리에 들어왔다 — **결론이 같아서 아무것도 깨지지 않은 채 조용히 전파된** 경우다.
 *
 * 🔖 되짚을 조건: 서버가 그 계산을 켜면 사유에 권한 문구가 들어온다 — 그때도 이 화면은 **읽기만**
 * 하면 되므로 바뀔 것이 없다. 반대로 **여기에 권한 AND를 넣고 싶어지면** 먼저 그 결정을 읽을 것.
 */
import { useState, useEffect, useRef } from 'react';
import { Button, Card, Modal } from '@safetyhub/ui';
import { api } from '../../lib/api';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG, type ModalLayer } from '../modalLayers';
import { sitesApi, type Site } from '../../lib/organizations';
import type { InspectionListItem } from '../../lib/inspections';

/**
 * 이 점검을 고를 수 없나 — **서버 판정을 읽기만** 한다(식·문구의 정본은 shared `resolveReportGate`).
 *
 * ⚠️ 폴백이 `false`(= 고를 수 있다)인 것이 규약이다. 없는 필드로 잠그면 그 응답 경로의 기능이
 * 통째로 꺼진다(`lib/inspections.ts`의 `reportGate` JSDoc이 정본 — 스케줄 응답엔 이 축이 **영구히
 * 없다**). 여기 식을 손으로 다시 쓰지 말 것 — 래칫은 `lib/__tests__/reportGate.test.ts`(⑧-b)다.
 */
function isReportGateBlocked(inspection: Pick<InspectionListItem, 'reportGate'>): boolean {
  return inspection.reportGate?.canGenerate === false;
}

interface InspectionSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (inspectionIds: string[], sortOrder: 'asc' | 'desc') => void;
  companyId: string;
  /** 점검 상세에서 진입 시 현장 ID (미리 설정됨) */
  siteId?: string;
  /** 점검 상세에서 진입 시 기본 선택 점검 ID */
  defaultInspectionId?: string;
  /**
   * 오버레이 층 — 층은 **여는 쪽이 정한다**(`modalLayers` 규약). 미지정 시 `MODAL_LAYER.sub`
   * (문서 유형 선택 모달 `z-[70]` 위 — 종전 하드코딩 값).
   */
  layer?: ModalLayer;
}

export function InspectionSelectModal({
  isOpen,
  onClose,
  onConfirm,
  companyId,
  siteId,
  defaultInspectionId,
  layer = MODAL_LAYER.sub,
}: InspectionSelectModalProps) {
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState(siteId || '');
  // `any[]`가 아닌 이유: `reportGate`를 읽어야 하고, `any`면 그 필드를 **오타로 잃어도 tsc가
  // 조용하다**(게이트가 사라져도 「전부 선택 가능」으로 보인다). 같은 `findAll` 응답을 점검 목록
  // 페이지도 이 타입으로 받는다.
  const [inspections, setInspections] = useState<InspectionListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    defaultInspectionId ? [defaultInspectionId] : [],
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);

  // ⛔ body `position:fixed` 잠금을 여기 두지 말 것 — **잠금 소유자는 opener(문서 유형 선택) 하나다.**
  // 이 모달은 그 위에서만 열리고, 자기 잠금을 겹쳐 걸면 열릴 때 이미 fixed인 body의 `scrollY`(=0)를
  // 저장해 `top`을 덮고, 닫힐 때 ''로 비워 **바깥이 열린 채 잠금이 풀리고** `scrollTo(0)`로 맨 위로
  // 튄다(이관 전 `3973b6cd0`부터 있던 결함). 여기서 거는 것은 base `Modal`의 `overflow` 잠금뿐이고, 그것은
  // 열릴 때 값을 저장해 닫힐 때 그 값으로 되돌린다. 탐지기:
  // `__tests__/ReportTypeSelectModal.bodyScrollLock.test.tsx`의 `[되돌림]` 케이스.
  // 🔖 되짚을 조건: 이 모달을 **다른 곳에서 단독으로** 여는 opener가 생기면 그 자리엔 fixed 잠금이 없다
  // (base의 overflow 잠금만 남는다). 그때 잠금을 여기로 되돌리지 말고 그 opener가 소유하게 하거나, 잠금을
  // 참조 수로 세는 공용 훅으로 한 벌만 두라 — 두 벌이면 위 중첩 닫힘 결함이 돌아온다.

  // siteId 없을 때 현장 목록 로드
  useEffect(() => {
    if (!isOpen || !companyId || siteId) return;
    setLoadingSites(true);
    sitesApi
      .findAll(companyId)
      .then((result) => setSites(result))
      .catch(() => setSites([]))
      .finally(() => setLoadingSites(false));
  }, [isOpen, companyId, siteId]);

  // 현장 선택 시(또는 siteId prop이 있을 때) 점검 목록 로드
  useEffect(() => {
    if (!isOpen || !companyId || !selectedSiteId) {
      setInspections([]);
      return;
    }
    setLoadingInspections(true);
    api
      .get<{ data?: InspectionListItem[] }>(
        `/inspections?companyId=${companyId}&siteId=${selectedSiteId}&status=COMPLETED,PENDING_REVIEW`,
      )
      .then((res) => {
        const loaded = res.data?.data || [];
        setInspections(loaded);
        // 막힌 점검은 **선택에서 덜어낸다** — 체크박스를 비활성으로 두는 것만으로는 부족하다:
        // `defaultInspectionId`가 미리 선택된 채로 열리고, 그 값이 막힌 점검일 수 있다(스케줄
        // 행 메뉴는 그 응답에 `reportGate`가 없어 「완료 + 권한」 폴백으로 문을 연다 —
        // `lib/inspections.ts`의 그 필드 JSDoc 표). 덜어내지 않으면 사용자가 아무것도 건드리지
        // 않고 「다음」을 눌러 서버 400까지 간다.
        const blockedIds = new Set(loaded.filter(isReportGateBlocked).map((insp) => insp.id));
        if (blockedIds.size > 0) {
          setSelectedIds((prev) => prev.filter((id) => !blockedIds.has(id)));
        }
      })
      .catch(() => setInspections([]))
      .finally(() => setLoadingInspections(false));
  }, [isOpen, companyId, selectedSiteId]);

  // 모달 열릴 때 상태 초기화
  useEffect(() => {
    if (!isOpen) return;
    setSelectedSiteId(siteId || '');
    setSelectedIds(defaultInspectionId ? [defaultInspectionId] : []);
    setSortOrder('asc');
    setSites([]);
    setInspections([]);
  }, [isOpen, siteId, defaultInspectionId]);

  // 현장 변경 시 선택된 점검 초기화
  const prevSiteIdRef = useRef(siteId || '');
  useEffect(() => {
    if (prevSiteIdRef.current !== selectedSiteId) {
      prevSiteIdRef.current = selectedSiteId;
      setSelectedIds(defaultInspectionId ? [defaultInspectionId] : []);
    }
  }, [selectedSiteId, defaultInspectionId]);

  const toggleInspection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  /**
   * 확정 — 고른 점검 id를 **정렬 옵션대로(createdAt asc/desc) 정렬해** 넘긴다. 클릭 순서가 아니라
   * 정렬된 순서가 곧 문서 안 점검 순서다: Composer 진입(`?inspectionIds=a,b`)은 이 순서를 그대로
   * junction `sortOrder`로 저장하고 서버 합본 조립이 그 순서로 항목을 잇는다(2026-09-14 다중 점검
   * 합본). 구 RA 페이지는 받은 뒤 같은 축으로 다시 정렬하므로 영향이 없다. 목록에 없는 id
   * (defaultInspectionId가 목록 밖일 때)는 맨 앞에 둔다.
   */
  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    const createdAtById = new Map<string, number>(
      inspections.map((insp) => [insp.id, new Date(insp.createdAt).getTime()]),
    );
    const sorted = [...selectedIds].sort((a, b) => {
      const ta = createdAtById.get(a);
      const tb = createdAtById.get(b);
      if (ta === undefined || tb === undefined) {
        if (ta === undefined && tb === undefined) return 0;
        return ta === undefined ? -1 : 1;
      }
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
    onConfirm(sorted, sortOrder);
  };

  // 층은 여는 쪽이 정한다(`layer` prop) — 유일한 opener는 문서 유형 선택(root `z-[70]`)이고 그 위 sub다.
  // 오버레이·Esc·포커스 트랩·aria·본문 스크롤(헤더·푸터 고정)은 base `Modal`이 소유한다.
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="포함할 점검 선택"
      size="lg"
      overlayZClassName={layer}
      overlayBgClassName={SUB_MODAL_OVERLAY_BG}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          {/* ⚠️ **`loadingInspections` 항을 지우지 말 것 — 「불필요한 로딩 가드」가 아니다.**
              목록이 도착하기 전에는 **게이트 판정을 모른다**. 그런데 `defaultInspectionId`는 미리
              선택된 채로 열리므로, 그 틈에 누르면 **체크박스 비활성도 위 덜어내기도 걸리지 않은**
              선택이 그대로 확정된다(막힌 점검이면 서버가 400). 즉 막는 근거는 「처리 중」이 아니라
              **「아직 판정할 수 없다」**다.
              탐지기: `__tests__/InspectionSelectModal.reportGate.test.tsx`의
              `[되돌림: 로딩 게이트]` 케이스(게이트 **없는** 픽스처로 재므로 게이트 읽기 축과 섞이지
              않는다 — 도착 후 버튼이 **열리는 것**까지 단언해 영구 잠금이 아님도 함께 지킨다). */}
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || loadingInspections}
          >
            다음{selectedIds.length > 0 ? ` (${selectedIds.length}개 선택)` : ''}
          </Button>
        </>
      }
    >
      {/* 현장 선택 (siteId prop이 없을 때만) */}
      {!siteId && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-ink-700 mb-1.5">
            현장 선택
          </label>
          {loadingSites ? (
            <div className="flex items-center gap-2 text-sm text-ink-500 py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500" />
              현장 목록 불러오는 중...
            </div>
          ) : (
            <select
              className="w-full border border-ink-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
            >
              <option value="">현장을 선택하세요</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* 점검 목록 */}
      {selectedSiteId && (
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1.5">
            점검 선택
            {selectedIds.length > 0 && (
              <span className="ml-2 text-brand-600 font-normal">
                ({selectedIds.length}개 선택)
              </span>
            )}
          </label>

          {loadingInspections ? (
            <Card
              surface="soft"
              elevation="none"
              padding="none"
              className="flex items-center justify-center gap-2 text-sm text-ink-500 py-8"
            >
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500" />
              점검 목록 불러오는 중...
            </Card>
          ) : inspections.length === 0 ? (
            <Card
              surface="soft"
              elevation="none"
              padding="none"
              className="text-sm text-ink-500 py-8 text-center"
            >
              선택한 현장에 완료된 점검이 없습니다.
            </Card>
          ) : (
            <Card
              padding="none"
              elevation="none"
              className="max-h-64 overflow-y-auto divide-y"
            >
              {[...inspections]
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((insp, idx) => {
                const checked = selectedIds.includes(insp.id);
                const round = idx + 1; // 차수 (오래된 순 인덱스)
                // 서버 판정 — 막힌 점검은 고를 수 없고 **왜인지 그 자리에서 말한다**.
                const blocked = isReportGateBlocked(insp);
                const reason = insp.reportGate?.disabledReason;
                const reasonId = `inspection-select-reason-${insp.id}`;
                return (
                  <label
                    key={insp.id}
                    className={`flex items-center px-4 py-3 transition-colors ${
                      blocked
                        ? 'cursor-not-allowed bg-surface-soft'
                        : checked
                          ? 'cursor-pointer bg-brand-50'
                          : 'cursor-pointer hover:bg-ink-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={blocked}
                      onChange={() => toggleInspection(insp.id)}
                      aria-describedby={blocked && reason ? reasonId : undefined}
                      className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 mr-3 flex-shrink-0 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          blocked ? 'text-ink-500' : 'text-ink-900'
                        }`}
                      >
                        {round}차 · {new Date(insp.createdAt).toLocaleDateString('ko-KR')}
                      </p>
                      <p className="text-xs text-ink-500">
                        {insp.status === 'COMPLETED' ? '완료' : '검토대기'}
                        {insp._count?.findings
                          ? ` · 발견사항 ${insp._count.findings}건`
                          : ''}
                        {insp._count?.photos
                          ? ` · 사진 ${insp._count.photos}건`
                          : ''}
                      </p>
                      {/* 사유는 **줄바꿈으로 흘린다** — `truncate`를 쓰면 「무엇을 해야 하나」가
                          잘리고, 잘림은 jsdom에 레이아웃 엔진이 없어 어느 spec도 못 잡는다. */}
                      {blocked && reason && (
                        <p id={reasonId} className="mt-1 text-xs font-medium text-amber-700">
                          {reason}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}
            </Card>
          )}

          {/* 정렬 순서 (2개 이상 선택 시) */}
          {selectedIds.length > 1 && (
            <div className="mt-3 flex items-center gap-2">
              <label className="text-sm text-ink-600 flex-shrink-0">정렬:</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="text-sm border border-ink-300 rounded-control px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="asc">오래된 점검 먼저</option>
                <option value="desc">최신 점검 먼저</option>
              </select>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default InspectionSelectModal;
